import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
// import { Player } from 'bitmovin-player';
import moment from 'moment';
import { Player } from 'bitmovin-player/modules/bitmovinplayer-core';
import EngineBitmovinModule from 'bitmovin-player/modules/bitmovinplayer-engine-bitmovin';
import MseRendererModule from 'bitmovin-player/modules/bitmovinplayer-mserenderer';
import HlsModule from 'bitmovin-player/modules/bitmovinplayer-hls';
import DashModule from 'bitmovin-player/modules/bitmovinplayer-dash';
import AbrModule from 'bitmovin-player/modules/bitmovinplayer-abr';
import XmlModule from 'bitmovin-player/modules/bitmovinplayer-xml';
import ContainerTSModule from 'bitmovin-player/modules/bitmovinplayer-container-ts';
import ContainerMp4Module from 'bitmovin-player/modules/bitmovinplayer-container-mp4';
import SubtitlesModule from 'bitmovin-player/modules/bitmovinplayer-subtitles';
import PolyfillModule from 'bitmovin-player/modules/bitmovinplayer-polyfill';
import StyleModule from 'bitmovin-player/modules/bitmovinplayer-style';
import Crypto from 'bitmovin-player/modules/bitmovinplayer-crypto';
import SubtitlesWebVTT from 'bitmovin-player/modules/bitmovinplayer-subtitles-vtt';
import EngineNative from 'bitmovin-player/modules/bitmovinplayer-engine-native';
import EngineWebRtc from 'bitmovin-player/modules/bitmovinplayer-engine-webrtc';
import { UIFactory } from 'bitmovin-player-ui';
import { RootState } from 'store';
import { useSelector } from 'react-redux';

// import 'bitmovin-player/bitmovinplayer-ui.css';
import 'bitmovin-player-ui/dist/css/bitmovinplayer-ui.min.css';

import { isMobile } from 'react-device-detect';

import useActiveWorkspace from 'hooks/useActiveWorkspace';

// import { toggleActivePlayer } from 'store/secondaryViewSlice';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import VideoButtons from 'pages/VideoV2/_components/videoButtons';
import { useNavigate } from 'react-router-dom';

import { fetchVideo, fetchVideoDetail } from 'api/videosAPI';
import { toggleActivePlayer } from 'store/secondaryChannelVideoSlice';

interface BitmovinPlayerContextValue {
  player: any;
  setPlayerSource: (source: string, channelId: string, seekTime: number) => void;
}

const BitmovinPlayerContext = createContext<BitmovinPlayerContextValue | undefined>(undefined);

export const useBitmovinPlayer = () => {
  const context = useContext(BitmovinPlayerContext);
  if (!context) {
    throw new Error('useBitmovinPlayer must be used within a BitmovinPlayerProvider');
  }
  return context;
};

interface BitmovinPlayerProviderProps {
  children: React.ReactNode;
}

export const BitmovinPlayerProvider: React.FC<BitmovinPlayerProviderProps> = ({ children }) => {
  const playerRef = useRef(null as any);
  const playerReady = useRef(null as any);
  const playerDiv = useRef(document.createElement('div'));
  const currentChannelIdRef = useRef<string | null>(null); // Track the current channelId

  const { activeWorkspace } = useActiveWorkspace();
  const userToken = activeWorkspace.user.access;
  const dispatch = useDispatch();
  const location = useLocation();
  const { workspaceId } = useParams();
  const [video, setVideo] = useState(null as any);
  const videoMatch = location.pathname.match(/\/video\/([^/]+)/);
  const videoId = videoMatch ? videoMatch[1] : undefined;
  const channelMatch = location.pathname.match(/\/channel\/([^/]+)/);
  const channelId = channelMatch ? channelMatch[1] : undefined;

  const [player, setPlayer] = useState(null as any);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDownload, setIsDownload] = useState<boolean>(false);
  const uiManagerRef = useRef<any>(null); // Store uiManager

  const playlistVideos = useSelector((state: RootState) => state.channelPlaylists.channels);
  const activities = useSelector((state: RootState) => state.userChannelActivity);
  const videos = useSelector((state: RootState) => state.userChannelVideo);

  const currentChannelId = currentChannelIdRef.current;

  const videosForChannel = typeof currentChannelId === 'string' ? videos[currentChannelId] : undefined;
  const activitiesForChannel = typeof currentChannelId === 'string' ? activities[currentChannelId] : undefined;
  const playlistVideosForChannel = typeof currentChannelId === 'string' ? playlistVideos[currentChannelId] : undefined;

  useEffect(() => {
    const fetchVideoInfo = async (videoHashId: string) => {
      const data =
        channelId === 'home'
          ? await fetchVideo({ videoHashId })
          : await fetchVideoDetail({ channelHashId: channelId, videoHashId });

      setVideo(data);
    };

    if (videoId && !location.state?.video) {
      fetchVideoInfo(videoId);
    } else {
      setVideo(location.state?.video);
    }
    return () => {
      setVideo(null);
    };
  }, [location]);

  useEffect(() => {
    if (typeof currentChannelId === 'string' && videoId) {
      const video =
        videosForChannel?.results.find((video: any) => video.hash_id === videoId) ||
        activitiesForChannel?.results.find((item: any) => item?.content_object?.hash_id === videoId)?.content_object ||
        playlistVideosForChannel?.find((video: any) => video.hash_id === videoId);
      if (video) {
        setVideo(video);
      }
    }
  }, [videosForChannel, activitiesForChannel, playlistVideosForChannel, videoId, currentChannelId]);

  useEffect(() => {
    // const playerConfig = {
    //   key: `${process.env.REACT_APP_BIT_MOVIN_KEY}`,
    //   network: {
    //     preprocessHttpRequest: function (type: any, request: any) {
    //       if (type == 'key/hls/aes') {
    //         request.headers['Authorization'] = `Bearer ${userToken}`;
    //       }
    //       return Promise.resolve(request);
    //     }
    //   }
    // };

    const playerConfig = {
      key: `${process.env.REACT_APP_BIT_MOVIN_KEY}`,
      playback: {
        autoplay: true
      },
      network: {
        preprocessHttpRequest: function (type: any, request: any) {
          request.headers = {
            ...request.headers,
            Authorization: `Bearer ${userToken}`
          };
          return request;
        }
      }
    };

    function setupPlayer() {
      Player.addModule(EngineBitmovinModule);
      Player.addModule(MseRendererModule);
      Player.addModule(HlsModule);
      Player.addModule(XmlModule);
      Player.addModule(DashModule);
      Player.addModule(AbrModule);
      Player.addModule(ContainerTSModule);
      Player.addModule(ContainerMp4Module);
      Player.addModule(SubtitlesModule);
      Player.addModule(PolyfillModule);
      Player.addModule(StyleModule);
      Player.addModule(Crypto);
      Player.addModule(SubtitlesWebVTT);

      if (isMobile) {
        Player.addModule(EngineNative);
        Player.addModule(EngineWebRtc);
      }

      const playerInstance = new Player(playerDiv.current, playerConfig);
      uiManagerRef.current = UIFactory.buildDefaultUI(playerInstance); // Build UI initially
      playerRef.current = playerInstance;
      setPlayer(playerInstance);
    }

    setupPlayer();

    const handleOnline = () => {
      if (playerRef.current) {
        console.log('re initialize player');
        uiManagerRef.current?.release();
        uiManagerRef.current = UIFactory.buildDefaultUI(playerRef.current); // Build UI initially
      }
    };
    const handleOffline = () => {
      console.log('offline');
    };

    // Listen for online and offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [userToken]);

  const setPlayerSource = (source: any, channelId: string, seekTime: number) => {
    currentChannelIdRef.current = channelId;

    if (playerRef.current && source?.hls_location) {
      playerReady.current = true;
      setIsDownload(true);
      setLoading(true);

      playerRef.current
        .load({
          hls: source.hls_location,
          subtitleTracks: [
            {
              id: source.id,
              label: 'English',
              lang: 'en',
              kind: 'captions',
              url: source.transcript
            }
          ]
        })
        .then(
          () => {
            // Ensure we are still on the correct channelId
            if (currentChannelIdRef.current === channelId) {
              setLoading(false);
              if (seekTime && playerRef.current) {
                playerRef.current.seek(seekTime);
              }
            }
          },
          (error: any) => {
            // Ensure we are still on the correct channelId
            if (currentChannelIdRef.current === channelId) {
              playerReady.current = true;
              setLoading(false);
              console.log('Error loading source:', error);
            }
          }
        );
    } else {
      playerReady.current = false;
      setLoading(false);
      setIsDownload(false);
      dispatch(toggleActivePlayer({ channelHashId: channelId, activePlayer: true }));
    }
  };

  const navigateTo = useNavigate();
  const navigatePlaylist = (playlist: any) => {
    dispatch(toggleActivePlayer({ channelHashId: currentChannelIdRef.current, activePlayer: false }));
    navigateTo(`/workspace/${workspaceId}/channel/${currentChannelIdRef.current}/playlist/${playlist.hash_id}`, {
      state: {
        videoElement: currentChannelIdRef.current + '-' + video.id + '-' + playlist.hash_id
      }
    });
  };

  const navigateChannel = (channelHashId: string) => {
    navigateTo(
      channelHashId !== 'home'
        ? `/workspace/${workspaceId}/user/channel/${channelHashId}`
        : `/workspace/${workspaceId}/videos/all`,
      {
        state: {
          fromNotification: true,
          videoElement: (channelHashId ? channelHashId : 'home') + '-' + video.id,
          channelHashId: channelHashId ? channelHashId : 'home'
        }
      }
    );
  };

  const [videpageHeight, setVideoPageHeight] = useState<number | null>(null);
  // const [videopageHeight, setVideopageHeight] = useState<number | null>(null);

  useEffect(() => {
    const updateHeight = () => {
      const headerHeight = 44; // Fixed header height
      const calculatedHeight = window.innerHeight - headerHeight;
      setVideoPageHeight(calculatedHeight);
    };

    // Initial calculation
    updateHeight();

    // Add resize event listener
    window.addEventListener('resize', updateHeight);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  const [isOverflow, setIsOverflow] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const descriptionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const checkOverflow = () => {
      const element = descriptionRef.current;
      if (element) {
        setIsOverflow(element.scrollHeight > element.clientHeight);
      }
    };

    checkOverflow();

    return () => {
      setIsOverflow(false);
      setIsExpanded(false);
    };
  }, [video?.description, loading]);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const [videoInfoHeight, setVideoInfoHeight] = useState(0);
  useEffect(() => {
    requestAnimationFrame(() => {
      if (containerRef.current) {
        const height = containerRef.current.clientHeight;
        if (videpageHeight && height) {
          setVideoInfoHeight(videpageHeight - height);
        }
      }
    });
  }, [videpageHeight, videoId, isExpanded]);

  return (
    <>
      <div className={`${videoId ? 'overflow-y-auto' : ''}`} style={{ height: `${videpageHeight}px` }}>
        <BitmovinPlayerContext.Provider value={{ player, setPlayerSource }}>
          {children}
          {videoId && loading && (
            <div className={`w-full h-2/3 video-container relative ${isMobile ? 'p-1' : 'p-5'}`} ref={containerRef}>
              <div
                className={`bg-black text-white flex items-center justify-center pt-[55%] relative ${
                  isMobile ? '' : 'rounded-xl'
                }`}
              >
                <div id="bmpui-id-2134" className={`bmpui-ui-buffering-overlay ${isMobile ? '' : 'rounded-xl'}`}>
                  <div className="bmpui-container-wrapper">
                    <div id="bmpui-id-2135" className="bmpui-ui-buffering-overlay-indicator" role="img"></div>
                    <div id="bmpui-id-2136" className="bmpui-ui-buffering-overlay-indicator" role="img"></div>
                    <div id="bmpui-id-2137" className="bmpui-ui-buffering-overlay-indicator" role="img"></div>
                  </div>
                </div>
                <div id="bmpui-id-1489" className="bmpui-ui roudend-xl">
                  <div id="bmpui-id-203" className="bmpui-ui-playbacktoggle-overlay rounded-xl">
                    <div className="bmpui-container-wrapper">
                      <button
                        id="bmpui-id-204"
                        aria-label="Play/Pause"
                        className="bmpui-ui-hugeplaybacktogglebutton bmpui-off bmpui-no-transition-animations"
                        type="button"
                        aria-pressed="false"
                        role="button"
                      >
                        <span className="bmpui-label">Play/Pause</span>
                        <div className="bmpui-image"></div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div
            className={`w-full overflow-y-hidden ${isMobile ? 'p-1' : 'p-5'} video-container relative ${
              videoId && playerReady.current && !loading ? 'visible' : 'hidden'
            }`}
          >
            <div
              className={`flex ${!isMobile ? 'rounded-xl' : ''} `}
              id="player"
              ref={playerDiv}
              style={{
                display: videoId && playerReady.current && !loading ? 'block' : 'none'
              }}
            />
          </div>
        </BitmovinPlayerContext.Provider>

        {videoId && video && !loading && (
          <div
            className={`${isMobile ? 'mt-2' : ''}`}
            style={videoInfoHeight ? { height: `${videoInfoHeight}px` } : {}}
          >
            <div className="mx-4">
              <div className="w-full">
                <p
                  className={`text-black font-black flex items-center  ${
                    isMobile ? 'text-sm truncate' : 'text-lg line-clamp-2'
                  }`}
                >
                  {video.generated_title ? video.generated_title : video.name}
                </p>
              </div>
              <div className="flex mt-1">
                <div>
                  <div className="">
                    <div className="flex items-center w-full">
                      <div className="mr-[10px] mb-auto shrink-0 text-right leading-[1.4]">
                        <img
                          className="rounded-full mt-[2px] w-9 h-9"
                          src={video.owner?.avatar ? video.owner.avatar : '/assets/img/no-img.jpg'}
                          alt={video.owner?.avatar ? video.owner.display_name : 'Avatar unknown'}
                        />
                      </div>
                      <div className="w-full grow shrink -mr-2 -ml-4 pr-2 pl-4 leading-[1.4] flex flex-wrap flex-col">
                        <div>
                          <a className="mr-[5px] text-iconColor font-lato no-underline font-black text-[15px]">
                            {video?.owner?.display_name
                              ? video.owner?.display_name
                              : video?.owner?.name
                              ? video?.owner?.name
                              : 'Unknown'}
                          </a>
                        </div>
                        {currentChannelIdRef.current !== 'home' ? (
                          <p className="text-[12.5px] line-clamp-2">
                            <span>{video?.playlists?.length ? 'Collections: ' : 'Not added to any collections'}</span>
                            <>
                              {video.playlists?.map((pl: any, index: number) => {
                                const isLastItem = index === video.playlists.length - 1;
                                return (
                                  <span
                                    key={index}
                                    className="text-[#1664A3] font-bold hover:underline cursor-pointer"
                                    onClick={() => {
                                      navigatePlaylist(pl);
                                    }}
                                  >
                                    {pl.name}
                                    {!isLastItem && ', '}
                                  </span>
                                );
                              })}
                            </>
                          </p>
                        ) : (
                          <p className="text-[12.5px] line-clamp-2">
                            <span>{video?.channels?.length ? 'Shared in ' : 'Not shared in any channel'}</span>
                            <>
                              {video?.channels?.map((obj: any, index: number) => {
                                const isLastItem = index === video.channels.length - 1;
                                const { channel, is_archived } = obj;
                                return (
                                  <span
                                    key={index}
                                    className={`${
                                      is_archived ? '' : 'hover:underline cursor-pointer text-[#1664A3] font-bold'
                                    }`}
                                    onClick={() => {
                                      if (!is_archived) navigateChannel(channel.hash_id);
                                    }}
                                  >
                                    #{channel.name} {is_archived ? ' (archived)' : ''}
                                    {!isLastItem && ', '}
                                  </span>
                                );
                              })}
                            </>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {!isMobile && (
                  <div className="ml-auto">
                    {videoId && video && !loading && (
                      <VideoButtons
                        videoId={videoId}
                        workspaceId={workspaceId}
                        video={video}
                        isDownloadable={isDownload}
                      />
                    )}
                  </div>
                )}
              </div>
              {!isMobile && (
                <div className="mt-4 text-sm relative">
                  <div className=" bg-gray-100 px-3 py-3 rounded-xl relative">
                    <p className="text-[#111] font-semibold flex items-center">
                      Meeting time: {moment(new Date(video.meeting_time)).format('MMMM DD, YYYY hh:mm A')}
                    </p>
                    <div className={`${isExpanded ? 'overflow-y-auto' : 'overflow-hidden h-10'}`} ref={descriptionRef}>
                      <p>{video?.description}</p>
                    </div>

                    {video?.description &&
                      (isOverflow ? (
                        isExpanded ? (
                          <button
                            className="mt-2 pb-[40px] bg-gray-100 cursor-pointer hover:font-semibold"
                            onClick={toggleExpand}
                          >
                            Show Less
                          </button>
                        ) : (
                          <button
                            className="mt-2 bg-gray-100 absolute bottom-3 right-2 cursor-pointer hover:font-semibold"
                            onClick={toggleExpand}
                          >
                            ... Show More
                          </button>
                        )
                      ) : null)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
